import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Guideline {
  id: string;
  title: string;
  description: string;
  source: string;
  category: string;
  lastUpdated: string;
  tags: string[];
  url: string;
}

interface GuidelineCardProps {
  guideline: Guideline;
}

export default function GuidelineCard({ guideline }: GuidelineCardProps) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const navigate = useNavigate();

  const getAgencyColor = (source: string) => {
    switch (source) {
      case 'Fannie Mae':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'Freddie Mac':
        return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
      case 'HUD':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'VA':
        return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBookmarked(!isBookmarked);
  };

  const handleViewGuideline = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // All guidelines use the standard route
    navigate(`/guideline/${guideline.id}`);
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 hover:border-slate-600 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10 group">
      <div className="flex justify-between items-start mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getAgencyColor(guideline.source)}`}>
          {guideline.source}
        </span>
        <button
          onClick={handleBookmark}
          className={`p-2 rounded-lg transition-colors cursor-pointer ${
            isBookmarked 
              ? 'text-emerald-400 bg-emerald-400/10' 
              : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-700'
          }`}
        >
          <i className={`${isBookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-lg`}></i>
        </button>
      </div>

      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-emerald-400 transition-colors">
        {guideline.title}
      </h3>
      
      <p className="text-slate-400 mb-4 line-clamp-3 leading-relaxed">
        {guideline.description}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {guideline.tags.slice(0, 3).map((tag, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-slate-700 text-slate-300 rounded-md text-xs border border-slate-600"
          >
            {tag}
          </span>
        ))}
        {guideline.tags.length > 3 && (
          <span className="px-2 py-1 bg-slate-700 text-slate-400 rounded-md text-xs border border-slate-600">
            +{guideline.tags.length - 3} more
          </span>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-slate-400 mb-4">
        <div className="flex items-center space-x-4">
          <span className="flex items-center">
            <i className="ri-folder-line mr-1"></i>
            {guideline.category}
          </span>
        </div>
        <span className="text-xs">
          Updated {new Date(guideline.lastUpdated).toLocaleDateString()}
        </span>
      </div>

      <button
        onClick={handleViewGuideline}
        className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-500 font-medium transition-colors cursor-pointer whitespace-nowrap group-hover:shadow-lg flex items-center justify-center"
      >
        View Guideline
        <i className="ri-arrow-right-line ml-2"></i>
      </button>
    </div>
  );
}
